(function () {
  'use strict';

  const TIMEOUT_MS = 15000;
  const cancelled = () => new DOMException('Configuration sharing cancelled.', 'AbortError');
  const assertActive = signal => { if (signal.aborted) throw signal.reason || cancelled(); };
  const ignoreTrackingFailure = callback => {
    try { Promise.resolve(callback()).catch(() => {}); } catch (error) {}
  };

  // Bound the whole operation, including response parsing and a helper that
  // does not support AbortSignal. Callers also check cancellation before any
  // later queue write, UI update, or navigation.
  async function withTimeout(work, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort(signal?.reason || cancelled());
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, {once: true});
    const timer = setTimeout(() => controller.abort(new DOMException('Configuration sharing timed out.', 'TimeoutError')), TIMEOUT_MS);
    let rejectOnAbort;
    try {
      assertActive(controller.signal);
      return await Promise.race([
        Promise.resolve().then(() => { assertActive(controller.signal); return work(controller.signal); }),
        new Promise((resolve, reject) => {
          rejectOnAbort = () => reject(controller.signal.reason || cancelled());
          controller.signal.addEventListener('abort', rejectOnAbort, {once: true});
        }),
      ]);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      if (rejectOnAbort) controller.signal.removeEventListener('abort', rejectOnAbort);
    }
  }

  async function getShareUrl(config, {signal} = {}) {
    const savedConfig = JSON.stringify(config);
    const currentUrl = new URL(window.location.href);
    return withTimeout(async operationSignal => {
      const response = await fetch('/apps/maison-functions/scripts/bedconfig/share', {
        method: 'POST',
        body: JSON.stringify({config: savedConfig}),
        headers: {'Content-Type': 'application/json'},
        signal: operationSignal,
      });
      assertActive(operationSignal);
      if (!response.ok) throw new Error('Unable to create the configuration link.');
      const result = await response.json();
      assertActive(operationSignal);
      if (typeof result?.handle !== 'string' || !result.handle.trim() || result.handle === 'user-bedconfig-') {
        throw new Error('The configuration link response is invalid.');
      }
      const view = currentUrl.searchParams.get('view');
      currentUrl.search = '';
      currentUrl.hash = '';
      if (view) currentUrl.searchParams.set('view', view);
      currentUrl.searchParams.set('config', result.handle.replace('user-bedconfig-', ''));
      return currentUrl.toString();
    }, signal);
  }

  function whenReady(readiness, signal) {
    if (readiness?.status === 'ready') return null;
    return withTimeout(async operationSignal => {
      if (typeof readiness?.ensureReady !== 'function') throw new Error('Configuration restoration is not ready.');
      await readiness.ensureReady(operationSignal);
      assertActive(operationSignal);
      if (readiness.status !== 'ready') throw new Error('Configuration restoration failed.');
    }, signal);
  }

  async function saveConfiguration(store, email, options, signal) {
    const waiting = whenReady(store.bedConfigReadiness, signal);
    if (waiting) await waiting;
    assertActive(signal);
    const tracking = window.MaisonKlaviyo;
    if (typeof tracking?.buildSavedConfigPayload !== 'function' || typeof tracking?.trackWithEmail !== 'function') {
      throw new Error('Configuration email saving is unavailable.');
    }
    const config = JSON.parse(JSON.stringify(store.bedConfig));
    const isUpholstered = window.maisonBedConfigurator.productId === 15322601947511;
    // Snapshot metadata before requesting the link; the link snapshots the same
    // selection synchronously. Editing the page while waiting cannot mix them.
    const details = {
      configurator_type: isUpholstered ? 'upholsteredbed' : 'boxspringbed',
      product_category: isUpholstered ? 'Konf_Upholsteredbed' : 'Konf_Boxspringbed',
      email,
      price: store.bedConfigSummary.totalPrice,
      currency: window.Shopify?.currency?.active ?? 'EUR',
      delivery_time: options.deliveryText,
      locale: options.locale,
    };
    return withTimeout(async operationSignal => {
      const url = await store.bedConfigSummary.getShareUrl({signal: operationSignal, config});
      assertActive(operationSignal);
      const payload = tracking.buildSavedConfigPayload({...details, url});
      assertActive(operationSignal);
      // Resolution means the existing helper accepted the event into its local
      // queue. It does not confirm email delivery or remote Klaviyo processing.
      await tracking.trackWithEmail(email, 'SavedConfiguration', payload);
      assertActive(operationSignal);
      if (typeof tracking.track === 'function') {
        ignoreTrackingFailure(() => tracking.track('saveBedConfig', payload));
      }
      return {payload, shareUrl: url};
    }, signal);
  }

  function trackSaveAttempt() {
    if (typeof window.TriplePixel !== 'function') return;
    const path = window.location.pathname;
    if (path.includes('customized-bed')) ignoreTrackingFailure(() => window.TriplePixel('custom', 'konfigSave'));
    else if (path.includes('polsterbett-configurator')) ignoreTrackingFailure(() => window.TriplePixel('custom', 'konfigPolsterSave'));
  }

  function createSave(options) {
    return {
      showModal: false, loading: false, done: false, error: false, email: null,
      _requestId: 0, _controller: null,
      cancel() {
        this._requestId += 1;
        this._controller?.abort();
        this._controller = null;
        this.loading = false;
      },
      reset() {
        this.cancel();
        this.done = false;
        this.error = false;
        this.email = null;
      },
      close() { this.reset(); this.showModal = false; },
      beforeSubmit() { trackSaveAttempt(); },
      onSaved() { this.done = true; },
      onFailure() {},
      async submit() {
        if (this.loading || this.done || !this.email?.trim()) return false;
        const email = this.email.trim();
        const requestId = ++this._requestId;
        const controller = new AbortController();
        this._controller = controller;
        this.loading = true;
        this.error = false;
        try {
          this.beforeSubmit();
          await saveConfiguration(this.$store, email, options, controller.signal);
          if (requestId !== this._requestId || !this.showModal) return false;
          this.onSaved();
          return true;
        } catch (error) {
          if (requestId === this._requestId && this.showModal) {
            this.error = true;
            this.onFailure();
          }
          return false;
        } finally {
          if (requestId === this._requestId) {
            this.loading = false;
            this._controller = null;
          }
        }
      },
      init() {
        this.$watch('showModal', value => { if (!value) this.reset(); });
      },
      destroy() { this.reset(); },
    };
  }

  function createLeave(options) {
    const state = createSave(options);
    const resetSave = state.reset;
    return Object.assign(state, {
      pendingUrl: null, pendingTarget: null, _pendingNewTab: null,
      closePendingTab() {
        if (this._pendingNewTab && !this._pendingNewTab.closed) {
          try { this._pendingNewTab.close(); } catch (error) {}
        }
        this._pendingNewTab = null;
      },
      reset() {
        resetSave.call(this);
        this.closePendingTab();
        this.pendingUrl = null;
        this.pendingTarget = null;
      },
      open(detail) {
        this.reset();
        this.pendingUrl = detail?.url;
        this.pendingTarget = detail?.target || '_self';
        this.showModal = true;
      },
      beforeSubmit() {
        // Open synchronously with the user gesture; a failed/cancelled save
        // closes this placeholder and keeps the configurator open for retry.
        if (this.pendingTarget === '_blank' && !this._pendingNewTab) {
          this._pendingNewTab = window.open('about:blank', '_blank');
        }
      },
      onFailure() { this.closePendingTab(); },
      onSaved() { this.leave(); },
      leave() {
        const url = this.pendingUrl, target = this.pendingTarget, newTab = this._pendingNewTab;
        this._pendingNewTab = null;
        this.close();
        if (!url) return;
        if (newTab && !newTab.closed) newTab.location.href = url;
        else if (target === '_blank') window.open(url, '_blank');
        else window.location.href = url;
      },
    });
  }

  function createShare({qrImageUrl}) {
    return {
      save: false, showModal: false, url: null, loading: false, error: false,
      _image: null, _requestId: 0, _controller: null,
      get whatsappUrl() { return this.url ? `https://wa.me/?text=${encodeURIComponent(this.url)}` : null; },
      generateQR() {
        if (!this.$refs.qr) return;
        this.$refs.qr.innerHTML = '';
        if (this._image) this._image.onload = this._image.onerror = null;
        this._image = null;
        const whatsappUrl = this.whatsappUrl;
        if (!whatsappUrl || !this.showModal) return;
        const image = new Image();
        this._image = image;
        image.onload = () => {
          if (image !== this._image || whatsappUrl !== this.whatsappUrl || !this.showModal) return;
          // A QR/logo failure must leave the usable link and copy action intact.
          try {
            const canvas = window.qrcanvas.qrcanvas({data: whatsappUrl, logo: {image}});
            this.$refs.qr.innerHTML = '';
            this.$refs.qr.appendChild(canvas);
          } catch (error) {}
        };
        image.src = qrImageUrl;
      },
      reset() {
        this._requestId += 1;
        this._controller?.abort();
        this._controller = null;
        this.loading = false;
        this.error = false;
        this.url = null;
        this.generateQR();
      },
      close() { this.reset(); this.showModal = false; },
      async generateUrl() {
        if (this.loading) return false;
        const requestId = ++this._requestId;
        const controller = new AbortController();
        this._controller = controller;
        this.loading = true;
        this.error = false;
        this.url = null;
        try {
          const waiting = whenReady(this.$store.bedConfigReadiness, controller.signal);
          if (waiting) await waiting;
          assertActive(controller.signal);
          const config = JSON.parse(JSON.stringify(this.$store.bedConfig));
          const url = await withTimeout(signal => this.$store.bedConfigSummary.getShareUrl({signal, config}), controller.signal);
          if (requestId !== this._requestId || !this.showModal) return false;
          this.url = url;
          return true;
        } catch (error) {
          if (requestId === this._requestId && this.showModal) this.error = true;
          return false;
        } finally {
          if (requestId === this._requestId) {
            this.loading = false;
            this._controller = null;
          }
        }
      },
      init() {
        this.$watch('url', () => this.generateQR());
        this.$watch('showModal', value => { if (value) this.generateUrl(); else this.reset(); });
      },
      destroy() { this.reset(); },
    };
  }

  window.MaisonBedSharing = {getShareUrl, createSave, createShare, createLeave, whenReady};
})();
